import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';

import { Notice } from '@/components/admin/Notice';
import { ForbiddenState } from '@/components/admin/states';
import {
  EMPTY_TARGET_CRITERIA,
  fetchEventTarget,
  previewEventTarget,
  removeEventTargetMembers,
  setEventTarget,
  type EventTargetCriteria,
  type EventTargetMember,
} from '@/features/events/api';
import { eventSurveyTabs, type EventSurveyTabDef } from '@/features/events/eventSurveyTabs';
import { fetchCohorts, fetchCourses } from '@/features/masters/api';
import { MEMBER_STATUS_LABELS, type MemberStatus } from '@/features/members/api';
import { ApiError } from '@/lib/api/http';
import { formatDateTime } from '@/lib/datetime';

type SurveyEnabledFlags = {
  pre: boolean;
  assignment: boolean;
  post: boolean;
  post_assignment: boolean;
  day_survey?: boolean;
  director?: boolean;
};

/** 対象者タブ（指示書 §4: 期・ロール・タグ・個別追加・個別除外）. */
export function EventTargetTab({
  eventId,
  isDraft,
  typeCode,
  surveyEnabled,
}: {
  eventId: string;
  isDraft: boolean;
  /** 種別ごとの課題・アンケート区分名（D-329） */
  typeCode: string | null;
  /** この回で使う区分だけリンクを出す（D-341） */
  surveyEnabled: SurveyEnabledFlags;
}): ReactElement {
  const surveyLinkTabs = eventSurveyTabs(typeCode).filter(
    (tab) =>
      tab.kind !== 'director' &&
      surveyEnabled[tab.kind as keyof SurveyEnabledFlags] !== false,
  );
  const queryClient = useQueryClient();
  const target = useQuery({ queryKey: ['event-target', eventId], queryFn: () => fetchEventTarget(eventId) });
  // 期・コースマスタは下書きの条件編集でのみ使う（下書きは事務局しか到達しないため 403 にならない）
  const cohorts = useQuery({ queryKey: ['cohorts'], queryFn: fetchCohorts, enabled: isDraft });
  const courses = useQuery({ queryKey: ['courses'], queryFn: fetchCourses, enabled: isDraft });

  const [criteria, setCriteria] = useState<EventTargetCriteria>(EMPTY_TARGET_CRITERIA);
  const [includeText, setIncludeText] = useState('');
  const [excludedIds, setExcludedIds] = useState<string[]>([]);
  const [tagsText, setTagsText] = useState('');
  const [previewMembers, setPreviewMembers] = useState<EventTargetMember[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);

  useEffect(() => {
    if (target.data && !target.data.is_published) {
      setCriteria(target.data.criteria);
      setIncludeText(target.data.criteria.include_member_ids.join(', '));
      setExcludedIds(target.data.criteria.exclude_member_ids);
      setTagsText(target.data.criteria.tags.join(', '));
    }
  }, [target.data]);

  if (target.isPending) return <p className="text-wptext-muted">読み込み中…</p>;
  if (target.isError) {
    // 対象者の条件・スナップショットは事務局のみ参照可（D-042と同じく権限不足を明示）
    if (target.error instanceof ApiError && target.error.status === 403) {
      return <ForbiddenState />;
    }
    return <p className="text-wpnotice-error">読み込みに失敗しました。</p>;
  }

  if (target.data.is_published) {
    async function onRemoveMember(member: EventTargetMember): Promise<void> {
      if (
        !window.confirm(
          `${member.name} をこの回のグループから外します。案内メールとグループメッセージの閲覧権限がなくなります。よろしいですか？`,
        )
      ) {
        return;
      }
      setRemovingId(member.id);
      setError(null);
      setNotice(null);
      try {
        await removeEventTargetMembers(eventId, [member.id]);
        await queryClient.invalidateQueries({ queryKey: ['event-target', eventId] });
        setNotice(`${member.name} をグループから外しました。`);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'グループから外せませんでした。');
      } finally {
        setRemovingId(null);
      }
    }

    return (
      <div>
        {notice && <Notice kind="success">{notice}</Notice>}
        {error && <Notice kind="error">{error}</Notice>}
        <p className="mb-1 text-wptext-strong">
          このイベントの対象者は<strong>{target.data.matched_count}名</strong>
          {(target.data.removed_count ?? 0) > 0 && (
            <>
              （公開時 {target.data.members.length}名のうち{' '}
              <strong>{target.data.removed_count}</strong>名をグループから外しています）
            </>
          )}
          です。
        </p>
        <p className="mb-3 text-wptext-muted">
          対象者は公開したときの名簿で確定しています
          {target.data.snapshot_taken_at &&
            `（${formatDateTime(target.data.snapshot_taken_at)} に確定）`}
          。事前アンケート未回答などでグループから外す場合は、各行の操作から行ってください（分科会の運用フロー）。
        </p>
        <TargetMemberList
          eventId={eventId}
          members={target.data.members}
          surveyLinkTabs={surveyLinkTabs}
          removable
          removingId={removingId}
          onRemove={(member) => void onRemoveMember(member)}
        />
      </div>
    );
  }

  function currentCriteria(): EventTargetCriteria {
    return {
      ...criteria,
      tags: splitCsv(tagsText),
      include_member_ids: splitCsv(includeText),
      exclude_member_ids: excludedIds,
    };
  }

  function toggleMember(id: string, checked: boolean): void {
    setExcludedIds((prev) =>
      checked ? prev.filter((x) => x !== id) : prev.includes(id) ? prev : [...prev, id],
    );
  }

  function toggleAllMembers(checked: boolean): void {
    const ids = (previewMembers ?? []).map((m) => m.id);
    setExcludedIds((prev) =>
      checked ? prev.filter((id) => !ids.includes(id)) : [...new Set([...prev, ...ids])],
    );
  }

  function toggleCohort(id: string): void {
    setCriteria((prev) => ({
      ...prev,
      cohort_ids: prev.cohort_ids.includes(id)
        ? prev.cohort_ids.filter((c) => c !== id)
        : [...prev.cohort_ids, id],
    }));
  }

  function toggleCourse(id: string): void {
    setCriteria((prev) => ({
      ...prev,
      course_ids: prev.course_ids.includes(id)
        ? prev.course_ids.filter((c) => c !== id)
        : [...prev.course_ids, id],
    }));
  }

  function toggleStatus(status: string): void {
    setCriteria((prev) => ({
      ...prev,
      statuses: prev.statuses.includes(status)
        ? prev.statuses.filter((s) => s !== status)
        : [...prev.statuses, status],
    }));
  }

  async function onPreview(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      // 除外は画面のチェックで決める。API に渡すと一覧から消えて戻せなくなる（D-228）
      const result = await previewEventTarget(eventId, {
        ...currentCriteria(),
        exclude_member_ids: [],
      });
      setPreviewMembers(result.members);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'プレビューに失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  async function onSave(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await setEventTarget(eventId, currentCriteria());
      await queryClient.invalidateQueries({ queryKey: ['event-target', eventId] });
      setNotice('対象者を保存しました。');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '保存に失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      {!isDraft && (
        <Notice kind="warning" dismissible={false}>
          このイベントは下書きではないため対象者を変更できません。
        </Notice>
      )}
      {notice && (
        <Notice kind="success" onDismiss={() => setNotice(null)}>
          {notice}
        </Notice>
      )}
      {error && (
        <Notice kind="error" dismissible={false}>
          {error}
        </Notice>
      )}

      <fieldset disabled={!isDraft || busy} className="flex flex-col gap-4">
        <div>
          {/* 期とコースはセット（D-114）。両方絞り込めるようにする（D-161） */}
          <p className="mb-1 text-wptext-muted">期</p>
          <div className="flex flex-wrap gap-3">
            {(cohorts.data ?? []).map((c) => (
              <label key={c.id} className="flex items-center gap-1">
                <input
                  type="checkbox"
                  checked={criteria.cohort_ids.includes(c.id)}
                  onChange={() => toggleCohort(c.id)}
                />
                {c.name}
              </label>
            ))}
          </div>
        </div>

        <div>
          <p className="mb-1 text-wptext-muted">コース</p>
          <div className="flex flex-wrap gap-3">
            {(courses.data ?? []).map((c) => (
              <label key={c.id} className="flex items-center gap-1">
                <input
                  type="checkbox"
                  checked={criteria.course_ids.includes(c.id)}
                  onChange={() => toggleCourse(c.id)}
                />
                {c.name}
              </label>
            ))}
          </div>
        </div>

        <div>
          <p className="mb-1 text-wptext-muted">在籍状態</p>
          <div className="flex flex-wrap gap-3">
            {/* 内部コードを画面に出さない（D-083） */}
            {(Object.keys(MEMBER_STATUS_LABELS) as MemberStatus[]).map((s) => (
              <label key={s} className="flex items-center gap-1">
                <input
                  type="checkbox"
                  checked={criteria.statuses.includes(s)}
                  onChange={() => toggleStatus(s)}
                />
                {MEMBER_STATUS_LABELS[s]}
              </label>
            ))}
          </div>
        </div>

        <label className="block">
          <span className="mb-1.5 block text-wptext-muted">タグ（カンマ区切り）</span>
          <input
            value={tagsText}
            onChange={(e) => setTagsText(e.target.value)}
            className="w-full rounded-sm border border-wpborder px-3 py-2"
          />
        </label>
        <label className="block">
          <span className="mb-1.5 block text-wptext-muted">個別追加（会員IDをカンマ区切り）</span>
          <input
            value={includeText}
            onChange={(e) => setIncludeText(e.target.value)}
            className="w-full rounded-sm border border-wpborder px-3 py-2"
          />
        </label>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => void onPreview()}
            className="btn-secondary"
          >
            プレビュー
          </button>
          <button
            type="button"
            onClick={() => void onSave()}
            className="rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover"
          >
            保存
          </button>
          {previewMembers !== null && (
            <span className="text-wptext-muted">
              対象: {previewMembers.filter((m) => !excludedIds.includes(m.id)).length}名
            </span>
          )}
        </div>

        {previewMembers !== null && (
          <div>
            <p className="mb-2 text-wptext-muted">
              初期状態は全員対象です。チェックを外すとその人は対象から外れます。
            </p>
            <TargetMemberList
              eventId={eventId}
              members={previewMembers}
              surveyLinkTabs={surveyLinkTabs}
              excludedIds={excludedIds}
              onToggle={toggleMember}
              onToggleAll={toggleAllMembers}
            />
          </div>
        )}
      </fieldset>

      <p className="mt-3 text-wptext-muted">保存済みの対象人数: {target.data.matched_count}名</p>
    </div>
  );
}

function splitCsv(text: string): string[] {
  return text
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean);
}

/** 対象者の名簿（D-132）。人数だけでは誰が対象か確認できないため、名前で出す。 */
function TargetMemberList({
  eventId,
  members,
  surveyLinkTabs,
  excludedIds,
  onToggle,
  onToggleAll,
  removable,
  removingId,
  onRemove,
}: {
  eventId: string;
  members: EventTargetMember[];
  /** この回で使う課題・アンケート区分へのリンク（D-341） */
  surveyLinkTabs: EventSurveyTabDef[];
  excludedIds?: string[];
  onToggle?: (id: string, checked: boolean) => void;
  onToggleAll?: (checked: boolean) => void;
  removable?: boolean;
  removingId?: string | null;
  onRemove?: (member: EventTargetMember) => void;
}): ReactElement {
  if (members.length === 0) {
    return <p className="text-wptext-muted">対象者がいません。</p>;
  }
  const selectable = onToggle !== undefined && excludedIds !== undefined;
  const allChecked = selectable && members.every((m) => !excludedIds.includes(m.id));
  const showStatus = removable || members.some((m) => m.is_removed);
  return (
    <div className="overflow-x-auto rounded-sm border border-wpborder bg-white">
      <table className="w-full text-left">
        <caption className="sr-only">対象者一覧</caption>
        <thead className="bg-wpcanvas">
          <tr>
            {selectable && (
              <th className="w-10 px-3 py-2">
                <input
                  type="checkbox"
                  checked={allChecked}
                  aria-label="全員を対象にする"
                  onChange={(e) => onToggleAll?.(e.target.checked)}
                />
              </th>
            )}
            <th className="px-3 py-2 font-normal text-wptext-muted">氏名</th>
            <th className="px-3 py-2 font-normal text-wptext-muted">期</th>
            <th className="px-3 py-2 font-normal text-wptext-muted">コース</th>
            {showStatus && (
              <th className="px-3 py-2 font-normal text-wptext-muted">参加状態</th>
            )}
            {surveyLinkTabs.length > 0 && (
              <th className="px-3 py-2 font-normal text-wptext-muted">回答・結果</th>
            )}
            {removable && <th className="px-3 py-2 font-normal text-wptext-muted">操作</th>}
          </tr>
        </thead>
        <tbody>
          {members.map((member) => {
            const checked = !selectable || !excludedIds.includes(member.id);
            const removed = member.is_removed === true;
            return (
              <tr
                key={member.id}
                className={`border-t border-wpborder ${removed ? 'bg-wpcanvas text-wptext-muted' : ''}`}
              >
                {selectable && (
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={checked}
                      aria-label={`${member.name}を対象にする`}
                      onChange={(e) => onToggle(member.id, e.target.checked)}
                    />
                  </td>
                )}
                <td className="px-3 py-2">
                  <Link to={`/members/${member.id}`} className="text-wpaccent hover:underline">
                    {member.name}
                  </Link>
                </td>
                <td className="whitespace-nowrap px-3 py-2">{member.cohort_name ?? '—'}</td>
                <td className="whitespace-nowrap px-3 py-2">{member.course_name ?? '—'}</td>
                {showStatus && (
                  <td className="whitespace-nowrap px-3 py-2">
                    {removed ? 'グループから外しました' : '参加中'}
                  </td>
                )}
                {surveyLinkTabs.length > 0 && (
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-x-2 gap-y-1">
                      {surveyLinkTabs.map((tab) => (
                        <Link
                          key={tab.kind}
                          to={`/members/${member.id}/events/${eventId}#survey-${tab.kind}`}
                          className="text-wpaccent hover:underline"
                        >
                          {tab.label}
                        </Link>
                      ))}
                    </div>
                  </td>
                )}
                {removable && (
                  <td className="px-3 py-2">
                    {!removed && (
                      <button
                        type="button"
                        className="text-wpnotice-error hover:underline disabled:opacity-50"
                        disabled={removingId === member.id}
                        onClick={() => onRemove?.(member)}
                      >
                        {removingId === member.id ? '処理中…' : 'グループから外す'}
                      </button>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
